/**
 * Customer Case API
 *
 * CRUD operations for Cases owned by a Customer via the new
 * ownership model. Uses the /customer/{customerID}/cases endpoints.
 */

import { post, put, get } from 'aws-amplify/api';
import { processResponse, handleApiResponse } from './api_util';
import useSWR from 'swr';


// ──────────────────────────────────────────────────────────────────────
// Raw API calls
// ──────────────────────────────────────────────────────────────────────

async function createCustomerCase(customerID, caseData) {
    const payload = {
        apiName: 'LivisiblyOrgApi',
        path: `customer/${customerID}/cases`,
        options: {
            body: {
                case_data: caseData,
            },
        },
    };
    const restOperation = post(payload);
    const response = await restOperation.response;
    return processResponse(response);
}

async function getCustomerCases(customerID) {
    const payload = {
        apiName: 'LivisiblyOrgApi',
        path: `customer/${customerID}/cases`,
    };
    const restOperation = get(payload);
    const response = await restOperation.response;
    return processResponse(response);
}

async function getCustomerCase(customerID, caseID) {
    const payload = {
        apiName: 'LivisiblyOrgApi',
        path: `customer/${customerID}/cases/${caseID}`,
    };
    const restOperation = get(payload);
    const response = await restOperation.response;
    return processResponse(response);
}

async function updateCustomerCase(customerID, caseData) {
    const payload = {
        apiName: 'LivisiblyOrgApi',
        path: `customer/${customerID}/cases/${caseData.caseID}`,
        options: {
            body: {
                case_data: caseData,
            },
        },
    };
    const restOperation = put(payload);
    const response = await restOperation.response;
    return processResponse(response);
}


// ──────────────────────────────────────────────────────────────────────
// SWR hooks
// ──────────────────────────────────────────────────────────────────────

export function useGetCustomerCases(customerID, shouldFetch = true, refreshInterval = 0) {
    return useSWR(
        shouldFetch && customerID ? ['CUSTOMER_CASES', customerID] : null,
        () => getCustomerCases(customerID),
        { refreshInterval }
    );
}

export function useGetCustomerCase(customerID, caseID, shouldFetch = true) {
    return useSWR(
        shouldFetch && customerID && caseID
            ? ['CUSTOMER_CASES', customerID, caseID]
            : null,
        () => getCustomerCase(customerID, caseID)
    );
}


// ──────────────────────────────────────────────────────────────────────
// Mutation hooks
// ──────────────────────────────────────────────────────────────────────

export function useCreateCustomerCase() {
    return async (customerID, caseData) => {
        try {
            const response = await createCustomerCase(customerID, caseData);
            return handleApiResponse(
                ['CUSTOMER_CASES', customerID],
                'caseID',
                response,
                'create'
            );
        } catch (error) {
            console.error('Error creating customer case:', error);
            throw error;
        }
    };
}

export function useUpdateCustomerCase() {
    return async (customerID, caseData) => {
        try {
            const response = await updateCustomerCase(customerID, caseData);
            return handleApiResponse(
                ['CUSTOMER_CASES', customerID],
                'caseID',
                response,
                'update'
            );
        } catch (error) {
            console.error('Error updating customer case:', error);
            throw error;
        }
    };
}